import { AxiosResponse } from 'axios';
import { Box, Button, CircularProgress } from '@mui/material';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { ErrorHandlerContextActionTypes, useErrorHandlerContextDispatch } from 'contexts/error-handler/ErrorHandlerContext';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';

import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { runScenario } from 'views/bluviu-nutrition/features/scenarios/api/bluviu-nutrition';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import isScenarioDisabled from 'views/bluviu-nutrition/features/scenarios/utils/validate-scenario';

const PAGE_NAME_ID = 'bluViuNutrition.setUp';

const RunScenario = () => {
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const params = useParams<ScenariosParams>();
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const errorHandlerDispatch = useErrorHandlerContextDispatch();
  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
    scenarioStatusQuery: { refetch: refetchScenarioStatus, data: scenarioStatus },
  } = useScenarioState();

  const submitScenario = usePost<AxiosResponse, { globalCompanyId: string; scenarioId: string }>(
    (request) => runScenario(request.globalCompanyId, request.scenarioId),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [
        ['getScenario', account.company!.companyId, params.scenarioId],
        ['getScenarios', params.globalSiteId],
      ].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey,
        });
      });

      refetchScenarioStatus();
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      errorHandlerDispatch({
        type: ErrorHandlerContextActionTypes.ADD_ERRORS,
        payload: {
          errors: [
            {
              code: error.response?.status.toString(),
              message: error.response?.data as string,
            },
          ],
        },
      });
    }
  );

  // HANDLERS
  const handlePostScenario = () => {
    submitScenario.mutate({ globalCompanyId: account.company!.companyId, scenarioId: scenario!.id });
  };

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  return (
    <Box display={'flex'} justifyContent={'flex-end'}>
      <Button
        disabled={scenarioStatus?.scenarioIsRunning || isScenarioDisabled(scenario, submitScenario.isPending)}
        startIcon={
          submitScenario.isPending || scenarioStatus?.scenarioIsRunning ? <CircularProgress color="inherit" size={18} /> : null
        }
        onClick={() => handlePostScenario()}>
        <FormattedMessage id={`${PAGE_NAME_ID}.submit`} />
      </Button>
    </Box>
  );
};

export default RunScenario;
